import {
	Checkbox,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@exopengithub/exokit";
import { clsx } from "clsx";
import type { ReactNode } from "react";

export type DataTableColumn<Row> = {
	id: string;
	/**
	 * This column's grid track — `"min-content"`, `"10rem"`,
	 * `"minmax(240px, 1fr)"`. Kept next to the cell it sizes so a column is one
	 * object instead of a track string, a header and a cell in three files.
	 */
	width: string;
	/**
	 * Header cell content. Usually a sort/filter trigger the caller owns, which
	 * is why this is a node and not a label — the table takes no part in sort
	 * or filter state.
	 */
	header?: ReactNode;
	render: (row: Row) => ReactNode;
	headerClassName?: string | undefined;
	cellClassName?: string | undefined;
};

export type DataTableSelection<Row> = {
	isSelected: (row: Row) => boolean;
	/** Why the row cannot be ticked: its checkbox is disabled and this shows as a tooltip. */
	getRowDisabledReason?: ((row: Row) => string | undefined) | undefined;
	onToggleRow: (row: Row) => void;
	getRowLabel: (row: Row) => string;
	allSelected: boolean;
	someSelected: boolean;
	onToggleAll: () => void;
	/** The header checkbox's accessible name; constant, since the checked state already says what is selected. */
	toggleAllLabel: string;
	/** What a click on the header checkbox will do right now. */
	toggleAllTooltip: string;
};

export type DataTableProps<Row> = {
	columns: DataTableColumn<Row>[];
	rows: Row[];
	getRowKey: (row: Row) => string;
	/** Row-level background, e.g. a failed row's tint — replaces the default. */
	getRowClassName?: ((row: Row) => string | undefined) | undefined;
	/**
	 * Shown under the header when `rows` is empty — a filter that matched
	 * nothing. Never replaces the header, and never sits inside the grid, so
	 * the header keeps its own height whatever this renders.
	 */
	emptyState?: ReactNode;
	/** Under the rows: load-more, pagination. Outside the grid for the same reason. */
	footer?: ReactNode;
	/**
	 * A full-width row above the column headers — e.g. a free-text filter
	 * field. Caller-owned like `column.header`, for the same reason: which
	 * fields it searches varies per table.
	 */
	filterBar?: ReactNode;
	/**
	 * Renders a leading checkbox column — a select-all checkbox in the header and
	 * one per row. The table holds no selection state, so the caller owns it.
	 */
	selection?: DataTableSelection<Row> | undefined;
	label?: string | undefined;
	className?: string | undefined;
};

const SELECTION_COLUMN_WIDTH = "2.75rem";

const HEADER_CELL_CLASS =
	"flex items-center border-b border-grey-200 bg-grey-50 px-4 py-3 text-xs font-medium text-grey-500";

const FILTER_BAR_CLASS =
	"flex flex-col items-center gap-2 bg-grey-50 px-4 py-3";

const CELL_CLASS =
	"flex min-h-12 items-center whitespace-nowrap border-b border-grey-200 px-4 py-1 group-last:border-b-0";

/**
 * The shared grid table: one bordered surface, one grid so header and rows can
 * never drift out of alignment, and one place that decides where the
 * no-matches state goes.
 *
 * Presentational on purpose. Sort and filter state stays with the page —
 * columns hand in their own header node — because the pages that use this
 * model that state four different ways.
 */
export const DataTable = <Row,>({
	columns,
	rows,
	getRowKey,
	getRowClassName,
	emptyState,
	footer,
	filterBar,
	selection,
	label,
	className,
}: DataTableProps<Row>) => (
	<div className={clsx("border border-grey-200 bg-white", className)}>
		{filterBar && <div className={FILTER_BAR_CLASS}>{filterBar}</div>}
		<div className="overflow-x-auto">
			<div
				role="table"
				aria-label={label}
				className="grid min-w-fit"
				style={{
					gridTemplateColumns: [
						...(selection ? [SELECTION_COLUMN_WIDTH] : []),
						...columns.map((column) => column.width),
					].join(" "),
				}}
			>
				<div className="contents" role="row">
					{selection && (
						<div
							role="columnheader"
							className={clsx(HEADER_CELL_CLASS, "pl-5 pr-0")}
						>
							<Tooltip delay={300}>
								<TooltipTrigger asChild>
									<span className="inline-flex">
										<Checkbox
											ariaLabel={selection.toggleAllLabel}
											checked={selection.allSelected}
											indeterminate={
												selection.someSelected && !selection.allSelected
											}
											onChange={selection.onToggleAll}
										/>
									</span>
								</TooltipTrigger>
								<TooltipContent>{selection.toggleAllTooltip}</TooltipContent>
							</Tooltip>
						</div>
					)}
					{columns.map((column) => (
						<div
							key={column.id}
							role="columnheader"
							className={clsx(HEADER_CELL_CLASS, column.headerClassName)}
						>
							{column.header}
						</div>
					))}
				</div>
				{rows.map((row) => {
					const rowClassName = getRowClassName?.(row);
					const disabledReason = selection?.getRowDisabledReason?.(row);
					const rowCheckbox = selection && (
						<Checkbox
							ariaLabel={selection.getRowLabel(row)}
							checked={selection.isSelected(row)}
							disabled={disabledReason !== undefined}
							onChange={() => selection.onToggleRow(row)}
						/>
					);
					return (
						<div className="group contents" role="row" key={getRowKey(row)}>
							{selection && (
								<div
									role="cell"
									className={clsx(
										CELL_CLASS,
										"min-w-11 pl-5 pr-0",
										rowClassName ?? "bg-white group-hover:bg-grey-50",
									)}
								>
									{disabledReason ? (
										<Tooltip delay={300}>
											<TooltipTrigger asChild>
												<span className="inline-flex">{rowCheckbox}</span>
											</TooltipTrigger>
											<TooltipContent>{disabledReason}</TooltipContent>
										</Tooltip>
									) : (
										rowCheckbox
									)}
								</div>
							)}
							{columns.map((column) => (
								<div
									key={column.id}
									role="cell"
									className={clsx(
										CELL_CLASS,
										rowClassName ?? "bg-white group-hover:bg-grey-50",
										column.cellClassName,
									)}
								>
									{column.render(row)}
								</div>
							))}
						</div>
					);
				})}
			</div>
		</div>
		{rows.length === 0 && emptyState}
		{footer}
	</div>
);
